import React, { useState } from 'react';
import { Cancel as CancelIcon, ParkSale, RecallParkSale, Process as ProcessIcon, QuoteIcon } from '@markinson/uicomponents-v2/SvgIcons';
import FastEntry from '../../common/FastEntry';
import { DataGrid } from 'devextreme-react';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Paper, withStyles } from '@material-ui/core';
import styles from './Lines.styles';
import { ILinesHandle, ILinesProperties } from './Lines.properties';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { columns, FieldName } from './constants';
import { IFastEntryHandle, IFastLineResponse } from 'components/common/FastEntry/FastEntry.properties';
import { useCNContextSelector } from '../CreditNotes.context';
import { HotKeyNames } from 'utils/constants';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { useCalculateCreditNoteLine, useDeleteCreditNoteLine, useRetrieveCreditNoteLines, useCalculateFastLine, useCreateFastLine, useUpdateCreditNoteLine, useRetrieveCreditNotesSummary } from 'api/creditNotes/creditNotes';
import { usePriceOverrideAndKitValidations } from '../CreditNotes.hooks';
import BOMDialog from 'components/common/BOMDialog';
import { isScreenRequest } from 'api/utils';
import { IValidationDialogAction } from 'components/common/ValidationDialog/ValidationDialog.properties';
import InvoiceLineSelectionDialog from './ProductSelectionDialog/InvoiceLineSelectionDialog';
import { isNull } from 'utils/utils';

const RecallCreditNoteIcon = (itemProps) => <RecallParkSale {...itemProps} style={{ width: 35, height: 35, margin: 3 }} />;
const ParkCreditNoteIcon = (itemProps) => <ParkSale {...itemProps} style={{ width: 35, height: 35, margin: 3 }} />;

const Lines = (props: ILinesProperties, ref: React.Ref<ILinesHandle>) => {
    const { classes, changeSelectedTab } = props;
    const { setActionBar } = React.useContext(ActionBarContext);
    const params = new URLSearchParams(location.search);
    const dataGridInnerRef = React.useRef<DataGrid>();
    const FastEntryRef = React.useRef<IFastEntryHandle>(null);
    const WorkCreditNoteId = useCNContextSelector<'CreditNote'>((state) => state.CreditNote) || Number(params.get('CreditNote'));
    const CreditNoteContext = useCNContextSelector<'CreditNotesContext'>((state) => state.CreditNotesContext);
    const [errorMessages, setErrorMessages] = React.useState<any>([]);
    const [refreshData, setRefreshData] = React.useState<boolean>(false);
    const [openInvoiceLineSelectionDialog, setOpenInvoiceLineSelectionDialog] = React.useState<boolean>(false);
    const [invoiceLinesData, setInvoiceLines] = React.useState<any>(false);
    const [selectedInvoiceLine, setSelectedInvoiceLine] = React.useState<any>(null);
    const fetchLinesMutation = useRetrieveCreditNoteLines();
    const fetchSummary = useRetrieveCreditNotesSummary(WorkCreditNoteId);

    const createFastLineMutation = useCreateFastLine();

    const calculateFastLineMessageBoxActionsCallback = (action: IValidationDialogAction) => {
        switch (action.name) {
            case 'go back':
                onGoBack();
                break;

            case 'credit components':
                setKitStatus('c');
                break;

            case 'credit kit':
                setKitStatus('k');
                break;

            default:

        }
    };
    const calculateFastLineMutation = useCalculateFastLine(calculateFastLineMessageBoxActionsCallback);

    const {
        bomList,
        kitStatus,
        selectedBomCode,
        multipleBomOpen,
        multipleBomDialogApi,
        validate,
        setBomList,
        setKitStatus,
        setSelectedBomCode,
        setMultipleBomState
    } = usePriceOverrideAndKitValidations();
    const deleteLineMutation = useDeleteCreditNoteLine();
    const updateLineMutation = useUpdateCreditNoteLine();
    const calculateLineMutation = useCalculateCreditNoteLine();
    const [linesData, setLinesData] = useState([]);

    const centerIcons = [
        {
            label: 'Recall Credit Note',
            Icon: RecallCreditNoteIcon,
            action: 'RecallCreditNote',
            disabled: WorkCreditNoteId > 0,
            hotkey: HotKeyNames.CtrlR
        },
        {
            label: 'Load',
            Icon: QuoteIcon,
            action: 'Load',
            disabled: WorkCreditNoteId === 0 || CreditNoteContext?.Invoice === 0 || !CreditNoteContext?.Invoice,
            hotkey: HotKeyNames.CtrlL
        }
    ];

    const rightIcons = [
        {
            label: 'Park',
            Icon: ParkCreditNoteIcon,
            action: 'ParkCreditNote',
            disabled: WorkCreditNoteId === 0 || linesData?.length === 0 || !linesData,
            hotkey: HotKeyNames.CtrlP
        },
        {
            label: 'Cancel',
            Icon: CancelIcon,
            action: 'CancelCreditNote',
            iconStyle: WorkCreditNoteId === 0 ? undefined : { fill: 'rgba(178, 0, 0, 1)' },
            disabled: WorkCreditNoteId === 0,
            hotkey: HotKeyNames.CtrlBackspace
        },
        {
            label: 'Process',
            Icon: ProcessIcon,
            action: 'Process',
            iconStyle: (WorkCreditNoteId === 0 || linesData?.length === 0) ? undefined : { fill: 'rgba(109, 217, 0, 1)' },
            disabled: WorkCreditNoteId === 0 || linesData?.length === 0,
            hotkey: HotKeyNames.CtrlEnter
        }
    ];

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons,
                rightIcons
            });
        },
        [WorkCreditNoteId, linesData, CreditNoteContext]
    );
    React.useEffect(
        () => {
            if (refreshData) {
                dataGridInnerRef.current?.instance?.refresh();
                dataGridInnerRef.current?.instance?.cancelEditData();
                setRefreshData(false);
            }

        },
        [refreshData]
    );

    const refreshDataGrid = React.useCallback(
        async (messages?: any) => {
            setRefreshData(true);
            if (messages) {
                setErrorMessages(messages);
            }

        },
        [setErrorMessages, setRefreshData]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            refreshDataGrid,
            fastEntryResetToDefault: () => {
                FastEntryRef?.current?.resetToDefault();
            }
        }),
        [refreshDataGrid, FastEntryRef]
    );

    const fetchLines = React.useCallback(
        async (BatchPage, BatchSize, Sort) => {
            if (WorkCreditNoteId !== undefined && WorkCreditNoteId > 0) {
                try {
                    const lines = await fetchLinesMutation.mutateAsync({
                        WorkCreditNoteId: WorkCreditNoteId,
                        urlQueryParams: {
                            BatchPage,
                            BatchSize,
                            Sort
                        }
                    });

                    if (lines && lines?.CreditNoteGridLines && errorMessages && errorMessages.length > 0) {
                        errorMessages?.forEach((message) => {
                            if (message.MessageFile === 'BinLot' && message.MessageKey) {
                                lines?.CreditNoteGridLines?.filter((line: any) => line?.LineNumber === parseInt(message.MessageKey))?.forEach((matchedLine) => { (matchedLine as any).AlertMessage = message.MessageDetail; });
                            }
                        });
                    }

                    lines.CreditNoteGridLines = lines?.CreditNoteGridLines?.map((line) => ({
                        ...line,
                        DisableLine: line?.LineType === 'r',
                        DisableEditAction: line?.LineType === 'r',
                    }));

                    setLinesData(lines?.CreditNoteGridLines ?? []);

                    return lines?.CreditNoteGridLines ?? [];
                } catch (error) {
                    console.error('Error fetching lines:', error);

                    return null;
                }
            } else {
                return null;
            }
        },
        [WorkCreditNoteId, errorMessages]
    );

    async function updateCreditNotesLine(data: any): Promise<any> {
        const response = await updateLineMutation.mutateAsync({
            ...data,
            ProductId: Number(data?.ProductId),
            WorkCreditNoteId: WorkCreditNoteId,
            WorkCreditNoteLineId: data?.CreditNoteLineId
        });

        if (response?.Status) {
            refreshDataGrid();
            fetchSummary.refetch();
        } else {
            dataGridInnerRef.current.instance.cancelEditData();
        }
    }

    const deleteLine = React.useCallback(
        async (lineNumber: number, lineData: any) => {

            if (lineNumber && lineNumber !== 0) {
                const response = await deleteLineMutation.mutateAsync({
                    WorkCreditNoteLineId: lineData?.CreditNoteLineId
                });

                if (response?.Status) {
                    refreshDataGrid();
                    fetchSummary.refetch();
                }
            }
        },
        [WorkCreditNoteId]
    );

    async function calculateCreditNoteLine(line: any, field: keyof any): Promise<any> {
        const response = await calculateLineMutation.mutateAsync({
            ...line,
            ProductId: Number(line?.ProductId),
            WorkCreditNoteId: WorkCreditNoteId,
            WorkCreditNoteLineId: line?.CreditNoteLineId,
            urlQueryParams: {
                ChangedField: field,
            }
        });

        if (response?.Status) {
            if (!isNull(response?.CreditNoteGridLine?.inlineObject)) {
                const inlineObject = {
                    ...response.CreditNoteGridLine.inlineObject,
                    CreditNoteLineId: response?.WorkCreditNoteLineId,
                    CreditNoteId: response?.WorkCreditNoteId,
                };

                return { line: inlineObject, status: response.Status };
            } else {
                return { line, status: response.Status };
            }
        } else {
            return { line: {}, status: false };
        }
    }

    const linesDataSource = React.useMemo(
        () => createDataSource(
            'LineNumber',
            { fetch: fetchLines, update: updateCreditNotesLine },
        ),
        [fetchLines]
    );

    const onEditorPreparing = (e) => {
        if (e.dataField === 'Description') {
            e.editorOptions.disabled = e.row.data.DescriptionReadOnly;
        }
        if (e.dataField === 'Price') {
            e.editorOptions.disabled = e.row.data.PriceReadOnly;
        }
        if (e.dataField === 'Discount') {
            e.editorOptions.disabled = e.row.data.DiscountReadOnly;
        }
    };

    const calculateAndValidateFastEntryDetails = async (data: any): Promise<IFastLineResponse> => {

        const response = await calculateFastLineMutation.mutateAsync({
            ...data,
            WorkCreditNoteId: WorkCreditNoteId,
            Quantity: data?.OrderedQuantity || 1,
            Price: data?.DiscountedPrice,
            InvoiceLineNumber: data?.InvoiceLineNumber || selectedInvoiceLine || null,
            urlQueryParams: {
                ChangedField: FieldName[data?.field]
            }
        });

        validate({
            Forms: response.Forms,
            BomCode: response.CreditNoteFastLine?.BomCode,
            KitDetail: response?.KitDetails,
            KitStatus: response?.CreditNoteFastLine?.KitStatus,
            field: data?.field,
            multipleBomDialogApi: 'calculateFastEntry'
        });

        if (response?.Forms?.length > 0 && response.Forms?.[0]?.FormId === 'SelectInvoiceLine') {
            setOpenInvoiceLineSelectionDialog(true);
            setInvoiceLines(response?.InvoiceLines);
        }

        if (response?.CreditNoteFastLine) {
            if (response.CreditNoteFastLine?.BomCode) {
                setSelectedBomCode(response.CreditNoteFastLine?.BomCode);
            }

            if (response?.CreditNoteFastLine?.InvoiceLineNumber) {
                setSelectedInvoiceLine(response?.CreditNoteFastLine?.InvoiceLineNumber);
            }

            if (!response.CreditNoteFastLine?.EnablePrice) {
                FastEntryRef.current.disablePriceField();
            } else if (response?.CreditNoteFastLine?.EnablePrice) {
                FastEntryRef.current.enablePriceField();
            }
        }

        return { FastLineDetails: { ...response?.CreditNoteFastLine, DiscountedPrice: response?.CreditNoteFastLine?.Price, OrderedQuantity: response?.CreditNoteFastLine?.Quantity }, ...response };
    };

    const clearKittingDetail = () => {
        setKitStatus('');
        setBomList([]);
        setSelectedBomCode('');
        setSelectedInvoiceLine(null);
    };

    const onGoBack = () => {
        if (FastEntryRef.current) {
            FastEntryRef.current.resetToDefault();
        }
        clearKittingDetail();
    };

    const handleActionClick = (status: 'c' | 'k') => {
        setMultipleBomState(false);
        setKitStatus(status);

        if (!status) {
            if (multipleBomDialogApi === 'calculateFastEntry') {
                onGoBack();
            }

            return;
        }

        switch (multipleBomDialogApi) {
            case 'calculateFastEntry': {
                if (status === 'c') {
                    if (FastEntryRef.current) {
                        FastEntryRef.current.calculateFastLineEntry({ KitStatus: 'c' });
                    }
                } else if (status === 'k') {
                    if (FastEntryRef.current) {
                        FastEntryRef.current.calculateFastLineEntry({ KitStatus: 'k' });
                    }
                } else {
                    onGoBack();
                }

                break;
            }

            default:
        }
    };

    const onApply = async (data: any) => {

        const response = await createFastLineMutation.mutateAsync({
            ...data,
            WorkCreditNoteId: WorkCreditNoteId,
            Quantity: data?.OrderedQuantity || 1,
            Price: data?.DiscountedPrice,
            KitStatus: kitStatus,
            BomCode: selectedBomCode
        });

        return refreshLines(response);
    };

    const refreshLines = async (response) => {
        if (response?.Status && !isScreenRequest(response?.Forms)) {
            fetchSummary.refetch();
            await linesDataSource.reload();
            dataGridInnerRef.current?.instance?.refresh();
            dataGridInnerRef.current?.instance?.cancelEditData();
            setRefreshData(false);
            clearKittingDetail();
            FastEntryRef.current.resetToDefault();
        }
    };

    const onInvoiceLineSelection = async (data: any) => {
        setOpenInvoiceLineSelectionDialog(false);
        setInvoiceLines(null);
        if (data && data.length > 0) {
            const selectedInvoice = data[0];
            await FastEntryRef.current.calculateFastLineEntry({ ProductId: selectedInvoice.ProductId, InvoiceLineNumber: selectedInvoice.LineNumber });
        }
    };

    return (
        <div className={classes.parentContainer}>
            <Paper className={classes.fastEntryContainer}>
                <FastEntry
                    innerRef={FastEntryRef}
                    config={{
                        priceFieldAsLookup: false,
                        disableFastEntry: !WorkCreditNoteId,
                        module: 'CreditNotes',
                        params: {
                            KitStatus: kitStatus,
                            BomCode: selectedBomCode,
                            Warehouse: CreditNoteContext?.Warehouse
                        },
                    }}
                    handlers={
                        {
                            onProductChanged: clearKittingDetail,
                            onFastEntryFieldChanged: calculateAndValidateFastEntryDetails,
                            onApply: onApply,
                            onUndo: clearKittingDetail,
                        }
                    }
                />
            </Paper>
            <Paper className={classes.gridContainer}>
                <DynamicDataGrid
                    dataGridClassName={classes.grid}
                    columns={columns}
                    allowHeaderFilting={false}
                    calculateFieldValue={calculateCreditNoteLine}
                    allowUpdating={true}
                    detailOnClick={
                        ({ data }) => {
                            if (data?.LineNumber && data?.CreditNoteLineId && WorkCreditNoteId && changeSelectedTab) {
                                changeSelectedTab('LineDetails', {
                                    search: {
                                        CreditNote: WorkCreditNoteId,
                                        LineNumber: data?.LineNumber,
                                        WorkCreditNoteLineId: data?.CreditNoteLineId
                                    }
                                });
                            }

                            return null;
                        }
                    }
                    gridProps={{
                        dataSource: linesDataSource,
                        dataGridRef: dataGridInnerRef,
                        sorting: { mode: 'single' },
                        disableDrillDown: false,
                        disableEditing: false,
                        disableDelete: false,
                        onEditorPreparing: onEditorPreparing,
                    }}
                    onDelete={deleteLine}
                />
            </Paper>
            {multipleBomOpen &&
                <BOMDialog
                    open={multipleBomOpen}
                    onClose={() => setMultipleBomState(false)}
                    data={bomList}
                    kitStatus={kitStatus ? kitStatus.toLowerCase() : ''}
                    isCredit={true}
                    onActionClick={handleActionClick}
                    onSelectedRowChanged={setSelectedBomCode}
                />
            }

            {
                openInvoiceLineSelectionDialog ?
                    <InvoiceLineSelectionDialog
                        open={openInvoiceLineSelectionDialog}
                        dialogStyles={{
                            width: '1000px',
                            minHeight: 500,
                            maxHeight: 500,
                            height: '100%'
                        }}
                        title='Products Found on Invoice'
                        subTitle='Multiple invoice lines have been found for the product. Select the line to include in the Credit note.'
                        invoiceLines={invoiceLinesData}
                        onClose={() => { setOpenInvoiceLineSelectionDialog(false); }}
                        onOk={onInvoiceLineSelection}
                    /> : undefined
            }
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(Lines)));
